import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Chip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
  Stack,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../api/client';
import { getModernwashLegacyMembers } from '../api/modernwashLegacy';
import { actionBtnSx, providerChipSx, PROVIDER_LABELS } from '../styles/uiTokens';
import { isMaster } from '../utils/auth';
import { canGrantMembership } from '../components/GrantMembershipDialog';
import BulkGrantMembershipDialog from '../components/BulkGrantMembershipDialog';

// 토큰의 PROVIDER_LABELS 사용 (하위 호환)
const providerLabel = PROVIDER_LABELS;

/**
 * The dedicated ModernWash Jeonju console fixes this page to the V1 history
 * source. It must not look like an entitlement switch or a merged member list.
 */
const MembersPage = ({
  fixedDataSource = null,
  memberDetailBasePath = '/members',
  title,
  description,
}) => {
  const routerNavigate = useNavigate();
  const navigate = (to, options) => routerNavigate(to, options);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [grantTargets, setGrantTargets] = useState([]);
  const [bulkGrantOpen, setBulkGrantOpen] = useState(false);
  // 기존 ModernWash 값은 미러 DB가 아닌 원천에서 읽기 전용으로 표시한다.
  const [dataSource, setDataSource] = useState(fixedDataSource || 'mogoo');
  const isLegacySource = dataSource === 'modernwash';

  // 과다이용 필터
  const [highUsageMode, setHighUsageMode] = useState(false);
  const [highUsageList, setHighUsageList] = useState([]);
  const [highUsageLoading, setHighUsageLoading] = useState(false);
  const [threshold, setThreshold] = useState(15);
  const [form, setForm] = useState({
    memberEmail: '', memberPassword: '', memberPasswordConfirm: '',
    memberNickname: '', memberPhone: '',
  });

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, dataSource]);

  useEffect(() => {
    if (fixedDataSource && dataSource !== fixedDataSource) {
      setDataSource(fixedDataSource);
      setPage(1);
    }
  }, [fixedDataSource, dataSource]);

  const openMemberDetail = (memberId) => navigate(`${memberDetailBasePath}/${memberId}`);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (isLegacySource) {
        const legacy = await getModernwashLegacyMembers(params);
        const data = legacy.data || {};
        setMembers((data.members || []).map((member) => ({
          _id: member.id,
          memberNickname: member.nickname,
          memberEmail: member.email,
          memberPhone: member.phone,
          memberPoints: member.points,
          provider: member.provider,
        })));
        setTotal(data.total || 0);
      } else {
        const res = await api.get('/member/admin/members', { params });
        setMembers(res.data.members || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('회원 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleDataSourceChange = (_, nextSource) => {
    if (!nextSource) return;
    setDataSource(nextSource);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setHighUsageMode(false);
    setGrantTargets([]);
  };

  const handleHighUsageToggle = async () => {
    if (highUsageMode) {
      setHighUsageMode(false);
      return;
    }
    setHighUsageLoading(true);
    try {
      const res = await api.get('/member/admin/members/high-usage', { params: { threshold } });
      setHighUsageList(res.data);
      setHighUsageMode(true);
    } catch (err) {
      console.error('과다이용 조회 실패:', err);
    } finally {
      setHighUsageLoading(false);
    }
  };

  /**
   * [회원 삭제 권한] — 백엔드는 `@Roles('master')` 다 (members.controller.ts:352-353).
   *
   * 그동안 화면은 manager 에게도 삭제 버튼을 보여줬다. 누르면 이메일 재입력까지 다 시키고
   * 마지막에 403 으로 실패한다. 되돌릴 수 없는 액션을 "끝까지 진행시킨 뒤 거절"하는 흐름이라
   * 운영자가 삭제된 줄 알거나, 반대로 될 일을 안 된다고 오해할 수 있다.
   *
   * 회원 생성(`POST admin/members`)은 `@Roles('master','manager')` 라 게이트하지 않는다.
   * 서버가 허용하는 기능을 프론트가 막으면 반대 방향의 결함이 된다.
   *
   * ⚠ 이건 UX 이지 보안이 아니다. 실제 차단은 서버 RolesGuard 가 한다.
   */
  const canDeleteMember = isMaster();
  const canGrant = canGrantMembership();

  const isGrantTarget = (memberId) => grantTargets.some((member) => member._id === memberId);
  const toggleGrantTarget = (member) => {
    setGrantTargets((current) => {
      if (current.some((item) => item._id === member._id)) {
        return current.filter((item) => item._id !== member._id);
      }
      return current.length >= 50 ? current : [...current, member];
    });
  };
  const toggleCurrentPageTargets = () => {
    const unselected = members.filter((member) => !isGrantTarget(member._id));
    if (unselected.length === 0) {
      setGrantTargets((current) => current.filter((target) => !members.some((member) => member._id === target._id)));
      return;
    }
    setGrantTargets((current) => {
      const remaining = 50 - current.length;
      return [...current, ...unselected.slice(0, Math.max(0, remaining))];
    });
  };
  const currentPageSelectedCount = members.filter((member) => isGrantTarget(member._id)).length;

  // 회원 삭제는 되돌릴 수 없음 — 강한 확인 다이얼로그(이메일 재입력)로 변경.
  // 이전: window.confirm 1회만 — 실수 클릭 시 즉시 삭제 가능
  const [deleteDialog, setDeleteDialog] = useState({ open: false, member: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = (memberId, memberName) => {
    // 멤버 전체 객체를 다시 찾아서(메일 등) 다이얼로그에 전달
    const member = members.find((m) => m._id === memberId) || { _id: memberId, memberNickname: memberName };
    setDeleteDialog({ open: true, member });
    setDeleteConfirmText('');
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialog({ open: false, member: null });
    setDeleteConfirmText('');
  };

  const performDelete = async () => {
    const m = deleteDialog.member;
    if (!m) return;
    // 이메일 정확 일치 확인 — 실수로 다른 회원 삭제 차단
    if (m.memberEmail && deleteConfirmText.trim() !== m.memberEmail) {
      alert('이메일이 일치하지 않습니다.');
      return;
    }
    setDeleteLoading(true);
    try {
      await api.delete(`/member/admin/members/${m._id}`);
      closeDeleteDialog();
      fetchMembers();
    } catch (err) {
      const msg = err?.response?.data?.message;
      alert(msg || '삭제에 실패했습니다.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.memberEmail || !form.memberPassword || !form.memberNickname || !form.memberPhone) {
      alert('모든 필드를 입력해주세요.');
      return;
    }
    if (form.memberPassword !== form.memberPasswordConfirm) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }
    try {
      await api.post('/member/admin/members', {
        memberEmail: form.memberEmail,
        memberPassword: form.memberPassword,
        memberNickname: form.memberNickname,
        memberPhone: form.memberPhone,
        provider: 'custom',
      });
      alert('회원이 생성되었습니다.');
      setCreateOpen(false);
      setForm({ memberEmail: '', memberPassword: '', memberPasswordConfirm: '', memberNickname: '', memberPhone: '' });
      fetchMembers();
    } catch (err) {
      alert('회원 생성에 실패했습니다.');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      {/* 헤더: 모바일에선 세로 스택, 데스크톱에선 가로 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
            {title || '회원 관리'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description || (isLegacySource ? 'ModernWash 전주점 기존 회원 · 읽기 전용' : '모구 회원')} · 총 {total.toLocaleString()}명
          </Typography>
        </Box>
        {!isLegacySource && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            type="number"
            label="월 기준 횟수"
            value={threshold}
            onChange={(e) => setThreshold(Math.max(1, parseInt(e.target.value) || 1))}
            disabled={highUsageMode}
            sx={{ width: 110 }}
            slotProps={{ htmlInput: { min: 1 } }}
          />
          <Tooltip title={`최근 3개월 매월 ${threshold}회 이상 세차한 회원을 필터링합니다`}>
            <Button
              variant={highUsageMode ? 'contained' : 'outlined'}
              color="warning"
              startIcon={highUsageLoading ? <CircularProgress size={16} color="inherit" /> : <WarningAmberIcon />}
              onClick={handleHighUsageToggle}
              disabled={highUsageLoading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {highUsageMode ? `과다이용 ${highUsageList.length}명 보는 중` : '과다이용 필터'}
            </Button>
          </Tooltip>
        </Box>}
      </Box>

      {!fixedDataSource && <ToggleButtonGroup
        exclusive
        value={dataSource}
        onChange={handleDataSourceChange}
        size="small"
        color="primary"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="mogoo">모구 회원</ToggleButton>
        <ToggleButton value="modernwash">ModernWash 전주점 기존 회원</ToggleButton>
      </ToggleButtonGroup>}

      {highUsageMode && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          최근 3개월 매월 <strong>{threshold}회 이상</strong> 세차한 회원 <strong>{highUsageList.length}명</strong>이 감지됐습니다.
          버튼을 다시 클릭하면 전체 목록으로 돌아갑니다.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box component="form" onSubmit={handleSearch} sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            placeholder="이메일, 이름, 전화번호로 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            } }}
            sx={{ width: { xs: '100%', sm: 350 } }}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 100, display: { xs: 'none', md: 'inline-flex' } }}>
          <InputLabel>표시</InputLabel>
          <Select
            value={limit}
            label="표시"
            onChange={(e) => { setLimit(e.target.value); setPage(1); }}
          >
            <MenuItem value={10}>10개</MenuItem>
            <MenuItem value={20}>20개</MenuItem>
            <MenuItem value={50}>50개</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {canGrant && !highUsageMode && !isLegacySource && (
        <Paper variant="outlined" sx={{ mb: 2, px: 1.25, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            회원을 체크한 뒤 한 번에 이용권을 부여할 수 있습니다. 최대 50명
          </Typography>
          {grantTargets.length > 0 && (
            <Button size="small" color="inherit" onClick={() => setGrantTargets([])}>선택 해제</Button>
          )}
          <Button variant="contained" size="small" onClick={() => setBulkGrantOpen(true)} disabled={grantTargets.length === 0}>
            선택한 {grantTargets.length}명 이용권 부여
          </Button>
        </Paper>
      )}

      {/* ── 과다이용 필터: 모바일 카드 / 데스크탑 테이블 ── */}
      {highUsageMode && isMobile ? (
        /* 모바일: 과다이용 카드 리스트 */
        <Stack spacing={1.25}>
          {highUsageList.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#888', border: '2px solid #ed6c02', borderRadius: '12px' }}>
              해당 조건의 회원이 없습니다.
            </Paper>
          ) : (
            highUsageList.map((member, index) => (
              <Paper
                key={member._id}
                elevation={0}
                onClick={() => openMemberDetail(member._id)}
                sx={{
                  p: 2,
                  border: '2px solid #ed6c02',
                  borderRadius: '12px',
                  backgroundColor: '#fffde7',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#fff8e1' },
                }}
              >
                {/* 상단: 번호 + 이름 + 가입경로 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#888', minWidth: 18 }}>#{index + 1}</Typography>
                  <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                  <Typography fontWeight={700} sx={{ flex: 1, wordBreak: 'keep-all' }}>
                    {member.memberNickname || '-'}
                  </Typography>
                  <Box sx={providerChipSx(member.provider)}>
                    {providerLabel[member.provider] || member.provider}
                  </Box>
                </Box>

                {/* 연락처 */}
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: '#555', display: 'block', wordBreak: 'break-all' }}>
                    📧 {member.memberEmail || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#555' }}>
                    📞 {member.memberPhone || '-'}
                  </Typography>
                </Box>

                {/* 월별 횟수 칩들 */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
                  {member.monthlyCounts?.map(({ month, count }) => (
                    <Chip
                      key={month}
                      label={`${month} ${count}회`}
                      size="small"
                      color={count >= 20 ? 'error' : 'warning'}
                      variant="filled"
                      sx={{ fontSize: 11 }}
                    />
                  ))}
                </Box>

                {/* 하단: 합계 + 관리 버튼 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #ffe0b2', pt: 1 }}>
                  <Typography fontWeight={700} color="error.main">
                    3개월 합계: {member.totalCount}회
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMemberDetail(member._id);
                    }}
                  >
                    관리
                  </Button>
                </Box>
              </Paper>
            ))
          )}
        </Stack>
      ) : highUsageMode ? (
        <TableContainer component={Paper} elevation={0} sx={{ border: '2px solid #ed6c02' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                <TableCell width="5%" align="center" sx={{ fontWeight: 700 }}>No.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>사용자명</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>이메일</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>전화번호</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>가입경로</TableCell>
                {highUsageList[0]?.monthlyCounts?.map(({ month }) => (
                  <TableCell key={month} align="center" sx={{ fontWeight: 700 }}>{month}</TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 700 }}>3개월 합계</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {highUsageList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#888' }}>
                    해당 조건의 회원이 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                highUsageList.map((member, index) => (
                  <TableRow key={member._id} hover sx={{ backgroundColor: index % 2 === 0 ? '#fffde7' : 'white' }}>
                    <TableCell align="center" sx={{ color: '#888' }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        {member.memberNickname || '-'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#555' }}>{member.memberEmail || '-'}</TableCell>
                    <TableCell align="center" sx={{ color: '#555' }}>{member.memberPhone || '-'}</TableCell>
                    <TableCell align="center">
                      <Box sx={providerChipSx(member.provider)}>
                        {providerLabel[member.provider] || member.provider}
                      </Box>
                    </TableCell>
                    {member.monthlyCounts?.map(({ month, count }) => (
                      <TableCell key={month} align="center">
                        <Chip
                          label={`${count}회`}
                          size="small"
                          color={count >= 20 ? 'error' : 'warning'}
                          variant="filled"
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 700, color: 'error.main' }}>
                      {member.totalCount}회
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => openMemberDetail(member._id)}
                      >
                        관리
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : isMobile ? (
        /* ── 모바일: 카드 리스트 ── */
        <Stack spacing={1.5}>
          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#888' }}>로딩 중...</Paper>
          ) : members.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#888' }}>등록된 회원이 없습니다.</Paper>
          ) : (
            members.map((member) => (
              <Paper
                key={member._id}
                elevation={0}
                onClick={() => openMemberDetail(member._id)}
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#fafafa' },
                }}
              >
                {/* 상단: 이름 + 가입경로 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                  {canGrant && !isLegacySource && (
                    <Checkbox
                      size="small"
                      checked={isGrantTarget(member._id)}
                      disabled={!isGrantTarget(member._id) && grantTargets.length >= 50}
                      slotProps={{ input: { 'aria-label': `${member.memberNickname || '회원'} 선택` } }}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleGrantTarget(member)}
                    />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" fontWeight={700}>
                      {member.memberNickname || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {member.memberEmail || '-'}
                    </Typography>
                  </Box>
                  <Box sx={providerChipSx(member.provider)}>
                    {providerLabel[member.provider] || member.provider}
                  </Box>
                </Box>

                {/* 중간: 메타 */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5, fontSize: 12, color: 'text.secondary' }}>
                  <Box>📞 {member.memberPhone || '-'}</Box>
                  <Box>💰 {member.memberPoints || 0}P</Box>
                </Box>

                {/* 하단: 액션 */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={(e) => { e.stopPropagation(); openMemberDetail(member._id); }}
                    sx={actionBtnSx}
                  >
                    관리
                  </Button>
                  {canDeleteMember && !isLegacySource && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDelete(member._id, member.memberNickname); }}
                      sx={actionBtnSx}
                    >
                      삭제
                    </Button>
                  )}
                </Box>
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        /* ── 데스크톱: 일반 회원 테이블 ── */
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <Table sx={{ '& td[align="center"], & th[align="center"]': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                {canGrant && !isLegacySource && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={members.length > 0 && currentPageSelectedCount === members.length}
                      indeterminate={currentPageSelectedCount > 0 && currentPageSelectedCount < members.length}
                      disabled={members.length === 0}
                      slotProps={{ input: { 'aria-label': '현재 페이지 회원 전체 선택' } }}
                      onChange={toggleCurrentPageTargets}
                    />
                  </TableCell>
                )}
                <TableCell width="5%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>No.</TableCell>
                <TableCell width="15%" sx={{ fontWeight: 700 }}>사용자명</TableCell>
                <TableCell width="22%" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>이메일</TableCell>
                <TableCell width="15%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>전화번호</TableCell>
                <TableCell width="8%" align="center" sx={{ fontWeight: 700 }}>포인트</TableCell>
                <TableCell width="10%" align="center" sx={{ fontWeight: 700 }}>가입경로</TableCell>
                <TableCell width="15%" align="center" sx={{ fontWeight: 700 }}>관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canGrant && !isLegacySource ? 8 : 7} align="center" sx={{ py: 4 }}>
                    로딩 중...
                  </TableCell>
                </TableRow>
              ) : members.length > 0 ? (
                members.map((member, index) => (
                  <TableRow
                    key={member._id}
                    hover
                    onClick={() => openMemberDetail(member._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {canGrant && !isLegacySource && (
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={isGrantTarget(member._id)}
                          disabled={!isGrantTarget(member._id) && grantTargets.length >= 50}
                          slotProps={{ input: { 'aria-label': `${member.memberNickname || '회원'} 선택` } }}
                          onChange={() => toggleGrantTarget(member)}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ color: '#888', display: { xs: 'none', md: 'table-cell' } }}>
                      {(page - 1) * limit + index + 1}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {member.memberNickname || '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#555', display: { xs: 'none', md: 'table-cell' }, wordBreak: 'break-all' }}>
                      {member.memberEmail || '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#555', display: { xs: 'none', lg: 'table-cell' } }}>
                      {member.memberPhone || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {member.memberPoints || 0}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={providerChipSx(member.provider)}>
                        {providerLabel[member.provider] || member.provider}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          onClick={(e) => { e.stopPropagation(); openMemberDetail(member._id); }}
                          sx={actionBtnSx}
                        >
                          관리
                        </Button>
                        {canDeleteMember && !isLegacySource && (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); handleDelete(member._id, member.memberNickname); }}
                            sx={actionBtnSx}
                          >
                            삭제
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={canGrant && !isLegacySource ? 8 : 7} align="center" sx={{ py: 4, color: '#888' }}>
                    등록된 회원이 없습니다.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, gap: 2, flexWrap: 'wrap' }}>
        {totalPages > 1 ? (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
          />
        ) : <Box />}
        {!isLegacySource && (
          <Button variant="contained" onClick={() => setCreateOpen(true)} sx={actionBtnSx}>
            생성
          </Button>
        )}
      </Box>

      {/* 회원 생성 모달 */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>회원 생성</DialogTitle>
        <DialogContent>
          <TextField
            label="이메일"
            type="email"
            fullWidth
            value={form.memberEmail}
            onChange={(e) => setForm({ ...form, memberEmail: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="비밀번호"
            type="password"
            fullWidth
            value={form.memberPassword}
            onChange={(e) => setForm({ ...form, memberPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="비밀번호 확인"
            type="password"
            fullWidth
            value={form.memberPasswordConfirm}
            onChange={(e) => setForm({ ...form, memberPasswordConfirm: e.target.value })}
            error={form.memberPasswordConfirm && form.memberPassword !== form.memberPasswordConfirm}
            helperText={form.memberPasswordConfirm && form.memberPassword !== form.memberPasswordConfirm ? '비밀번호가 일치하지 않습니다.' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            label="닉네임"
            fullWidth
            value={form.memberNickname}
            onChange={(e) => setForm({ ...form, memberNickname: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="전화번호"
            fullWidth
            value={form.memberPhone}
            onChange={(e) => setForm({ ...form, memberPhone: e.target.value })}
            placeholder="010-1234-5678"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleCreate}>생성</Button>
        </DialogActions>
      </Dialog>

      <BulkGrantMembershipDialog
        open={bulkGrantOpen}
        initialMembers={grantTargets}
        onClose={() => setBulkGrantOpen(false)}
        onGranted={() => {
          setGrantTargets([]);
          fetchMembers();
        }}
      />

      {/* 회원 삭제 확인 — 이메일 재입력 강제 */}
      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          ⚠️ 회원 삭제
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            삭제하면 회원 정보·구독·세차 이력 연결이 끊깁니다. <b>되돌릴 수 없습니다.</b>
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', rowGap: 1, columnGap: 2, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">이름</Typography>
            <Typography variant="body2"><b>{deleteDialog.member?.memberNickname || '-'}</b></Typography>
            <Typography variant="caption" color="text.secondary">이메일</Typography>
            <Typography variant="body2">{deleteDialog.member?.memberEmail || '-'}</Typography>
            <Typography variant="caption" color="text.secondary">전화</Typography>
            <Typography variant="body2">{deleteDialog.member?.memberPhone || '-'}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            확인을 위해 회원의 <b>이메일</b>을 그대로 입력해주세요.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteDialog.member?.memberEmail || ''}
            error={!!deleteConfirmText && deleteConfirmText.trim() !== deleteDialog.member?.memberEmail}
            helperText={
              !!deleteConfirmText && deleteConfirmText.trim() !== deleteDialog.member?.memberEmail
                ? '이메일이 일치하지 않습니다.'
                : ''
            }
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>취소</Button>
          <Button
            onClick={performDelete}
            variant="contained"
            color="error"
            disabled={
              deleteLoading ||
              !deleteConfirmText.trim() ||
              (!!deleteDialog.member?.memberEmail && deleteConfirmText.trim() !== deleteDialog.member?.memberEmail)
            }
          >
            {deleteLoading ? '삭제 중...' : '영구 삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembersPage;
